// View-model for the compact jobs list (LogsPage). Every cell of a row is
// computed here as plain data so the renderers in components/JobsTable.tsx
// stay thin and the cell logic is unit-tested (jobsTable.test.ts).
// Design + width budgets: internal/compiler/plans/2026-10-14-jobs-table-compact.md

import {
  buildKindOf,
  fmtDurationMs,
  platformLabel,
  sequenceText,
  type JobBuildFields,
} from "./buildLabels.ts";
import {
  COMPILED_LINES_TOOLTIP,
  DYNAMIC_CODE_TOOLTIP_YES,
  LINES_OF_CODE_TOOLTIP,
  fmtLines,
} from "./codeMetrics.ts";
import { failureView, type FailureView } from "./failureClass.ts";
import {
  chipVerdict,
  type CacheFields,
  type CacheVerdict,
} from "./cacheVerdict.ts";

/** The fields of a jobs-list row the compact table reads. LogsPage's
 *  JobListRow satisfies this structurally (extra fields are ignored). */
export interface JobRowInput extends JobBuildFields, CacheFields {
  id: string;
  org_id: string;
  org_name: string | null;
  user_name: string | null;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  entry: string;
  total_lines_of_code: number | null;
  compiled_lines_of_code: number | null;
  dynamic_code: number | null;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  /** The job's artifacts were deleted with a published build (internal/api
   *  migration 0020; lib/jobStatus.ts): the chip reads "artifacts deleted". */
  artifacts_deleted_at?: number | null;
  /** Failure verdict (internal/api migration 0018; lib/failureClass.ts). */
  failure_class?: string | null;
  failure_cause?: string | null;
  bundle_key?: string | null;
  failure_backfilled?: boolean;
  /** Peak guest memory, MiB (internal/api migration 0017); null = unknown. */
  mem_peak_mb?: number | null;
}

/** Characters of the entry path shown before left-truncation (~44 chars of
 *  12px text fits the ~34% Entry column at a 1280px viewport). */
export const ENTRY_CHAR_BUDGET = 44;

/** Keep the END of a path (the filename matters most): "…/pkg/src/index.ts".
 *  Returns the text unchanged when it fits. */
export function truncateLeft(
  text: string,
  max: number = ENTRY_CHAR_BUDGET,
): string {
  if (text.length <= max) return text;
  return "…" + text.slice(text.length - (max - 1));
}

export interface EntryCell {
  text: string;
  full: string;
  truncated: boolean;
}

export function entryCell(entry: string): EntryCell {
  const text = truncateLeft(entry);
  return { text, full: entry, truncated: text !== entry };
}

export interface BuildCell {
  /** "#6 final (no LLVM PGO)" — null for an ordinary job with no build. */
  chip: string | null;
  chipTitle: string | null;
  /** Label computed from the row's fields rather than named by the submitter. */
  derived: boolean;
  /** "linux-x64 · musl" — null when the job says nothing about its target
   *  (old jobs without a build label and without platform columns). */
  platform: string | null;
}

export function buildCell(job: JobBuildFields): BuildCell {
  const info = buildKindOf(job);
  const platform =
    info || job.platform || job.libc || job.target ? platformLabel(job) : null;
  if (!info) return { chip: null, chipTitle: null, derived: false, platform };
  const seq = sequenceText(job.build_seq);
  return {
    chip: `${seq ? `#${seq} ` : ""}${info.label}`,
    chipTitle: info.derived
      ? `${info.label} (derived from the job's own fields; this job was submitted without a build label)`
      : `${info.label}${job.build_run ? ` — run ${job.build_run}` : ""}`,
    derived: info.derived,
    platform,
  };
}

export interface WhoCell {
  org: string;
  /** null = unknown user (rendered as an em dash). */
  user: string | null;
  /** Shown as a muted "System" with the full name in the tooltip. */
  isSystem: boolean;
  title: string;
}

export function whoCell(
  job: Pick<JobRowInput, "org_id" | "org_name" | "user_name">,
): WhoCell {
  const full = job.org_name ?? job.org_id;
  const isSystem = job.org_name != null && /^system\b/i.test(job.org_name);
  const org = isSystem
    ? "System"
    : (job.org_name ?? `${job.org_id.slice(0, 8)}…`);
  return {
    org,
    user: job.user_name,
    isSystem,
    title: `${full}${job.user_name ? ` — ${job.user_name}` : ""}`,
  };
}

/** Compact count: exact below 10,000, then "12.3k" / "1.2M". The tooltip
 *  always carries the exact numbers. */
export function fmtLinesCompact(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 10_000) return fmtLines(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 100_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export interface SizeCell {
  /** "112 / 112" (loaded / compiled), or "—" when neither is known. */
  text: string;
  /** Dynamic-code chip: only when the flag is set. */
  dynamic: boolean;
  title: string;
}

export function sizeCell(
  job: Pick<
    JobRowInput,
    "total_lines_of_code" | "compiled_lines_of_code" | "dynamic_code"
  >,
): SizeCell {
  const t = job.total_lines_of_code;
  const c = job.compiled_lines_of_code;
  const text =
    t == null && c == null
      ? "—"
      : `${fmtLinesCompact(t)} / ${fmtLinesCompact(c)}`;
  const dynamic = job.dynamic_code === 1;
  const title = [
    `Lines of code: ${fmtLines(t)}. ${LINES_OF_CODE_TOOLTIP}`,
    `Compiled: ${fmtLines(c)}. ${COMPILED_LINES_TOOLTIP}`,
    job.dynamic_code == null
      ? null
      : dynamic
        ? DYNAMIC_CODE_TOOLTIP_YES
        : "Dynamic code: no.",
  ]
    .filter(Boolean)
    .join("\n");
  return { text, dynamic, title };
}

export interface MemCell {
  /** "3.4 GiB" / "512 MiB", or "—" when the job has no telemetry. */
  text: string;
  title: string;
}

/** Peak guest memory (jobs.mem_peak_mb, migration 0017 — MemTotal -
 *  MemAvailable in MiB) as a compact figure; null/invalid -> "—". */
export function memCell(job: { mem_peak_mb?: number | null }): MemCell {
  const mb = job.mem_peak_mb;
  if (mb == null || !Number.isFinite(mb) || mb < 0) {
    return {
      text: "—",
      title:
        "Peak memory not recorded (job predates the memory sampler, or never reached a runner).",
    };
  }
  const text =
    mb < 1024
      ? `${Math.round(mb)} MiB`
      : `${(mb / 1024).toFixed(mb / 1024 < 10 ? 2 : 1)} GiB`;
  const bytes = Math.round(mb * 1024 * 1024);
  return {
    text,
    title: `Peak guest memory used: ${bytes.toLocaleString("en-US")} bytes (${mb.toLocaleString("en-US")} MiB). Guest-wide used memory (MemTotal - MemAvailable), sampled during the build.`,
  };
}

export interface FormatOpts {
  locale?: string;
  timeZone?: string;
}

function fmtStamp(ms: number, o: FormatOpts): string {
  return new Date(ms).toLocaleString(o.locale, {
    timeZone: o.timeZone,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function fmtFull(ms: number, o: FormatOpts): string {
  return new Date(ms).toLocaleString(o.locale, { timeZone: o.timeZone });
}

export interface WhenCell {
  /** Created time. */
  created: string;
  /** "took 5m 06s" | "running 2m 03s" | "queued 12s" | null. */
  detail: string | null;
  /** True while the detail is a live-ticking elapsed value. */
  live: boolean;
  title: string;
}

export function whenCell(
  job: Pick<
    JobRowInput,
    "status" | "created_at" | "started_at" | "finished_at"
  >,
  now: number,
  o: FormatOpts = {},
): WhenCell {
  let detail: string | null = null;
  let live = false;
  if (job.status === "running") {
    detail = `running ${fmtDurationMs(job.started_at ?? job.created_at, null, now)}`;
    live = true;
  } else if (job.status === "queued") {
    detail = `queued ${fmtDurationMs(job.created_at, null, now)}`;
    live = true;
  } else if (job.finished_at) {
    const d = fmtDurationMs(
      job.started_at ?? job.created_at,
      job.finished_at,
      now,
    );
    detail = d ? `took ${d}` : null;
  }
  const title = [
    `Created: ${fmtFull(job.created_at, o)}`,
    job.started_at ? `Started: ${fmtFull(job.started_at, o)}` : null,
    job.finished_at
      ? `Finished: ${fmtFull(job.finished_at, o)}`
      : "Not finished",
  ]
    .filter(Boolean)
    .join("\n");
  return { created: fmtStamp(job.created_at, o), detail, live, title };
}

export interface JobRowView {
  id: string;
  shortId: string;
  status: JobRowInput["status"];
  /** Artifacts deleted with a published build: show the "artifacts deleted"
   *  chip; status and the telemetry-based panels are the stored ones. */
  artifactsDeleted: boolean;
  entry: EntryCell;
  build: BuildCell;
  who: WhoCell;
  size: SizeCell;
  mem: MemCell;
  when: WhenCell;
  /** The failure chip + one-line cause (null unless the job failed with a verdict). */
  failure: FailureView | null;
  /** Warm-cache chip (migration 0019); see chipVerdict. */
  cache: CacheVerdict | null;
}

export function jobRowView(
  job: JobRowInput,
  now: number,
  o: FormatOpts = {},
): JobRowView {
  return {
    id: job.id,
    shortId: job.id.slice(0, 8),
    status: job.status,
    artifactsDeleted: job.artifacts_deleted_at != null,
    entry: entryCell(job.entry),
    build: buildCell(job),
    who: whoCell(job),
    size: sizeCell(job),
    mem: memCell(job),
    when: whenCell(job, now, o),
    failure: failureView(job),
    cache: chipVerdict(job),
  };
}

/** True when any row shows a live elapsed value (the table then ticks). */
export function hasLiveRows(jobs: readonly { status: string }[]): boolean {
  return jobs.some((j) => j.status === "running" || j.status === "queued");
}
